import React, { useState, useEffect } from 'react';
import { PublicHeader } from '../../components/public/PublicHeader';
import { PublicFooter } from '../../components/public/PublicFooter';
import { HeroSlider } from '../../components/public/HeroSlider';
import { CustomCursor } from '../../components/public/CustomCursor';
import { StargazeUniverse3D } from '../../components/public/StargazeUniverse3D';
import { ProductionTimeline } from '../../components/public/ProductionTimeline';
import { PostProductionSuite } from '../../components/public/PostProductionSuite';
import { ProjectDetailModal } from '../../components/public/ProjectDetailModal';
import { EquipmentDetailModal } from '../../components/public/EquipmentDetailModal';
import { StargazeImage } from '../../components/common/StargazeImage';
import { INITIAL_PROJECTS, INITIAL_EQUIPMENT, INITIAL_EVENTS, INITIAL_NEWS, INITIAL_DISTRIBUTION } from '../../data/mockData';
import { INITIAL_TEAM_MEMBERS } from '../../data/initialCmsData';
import { ProjectItem, EquipmentItem, EventItem, DistributionTitle, NewsArticle, PartnerItem, SiteContentConfig, TeamMember } from '../../types';
import { resolveProjectMedia, resolveEquipmentMedia, resolveEventMedia, resolveDistributionMedia, resolveNewsMedia } from '../../lib/mediaResolver';
import { getProjects, getEquipment, getEvents, getDistribution, getNewsArticles, getPartners, getSiteContent, getTeam } from '../../lib/cmsService';
import { Film, Camera, Sparkles, ArrowRight, Calendar, Play, ChevronRight, CheckCircle2, Globe, Award, Newspaper, ArrowUpRight, UserCheck, Users } from 'lucide-react';

export const HomePage: React.FC = () => {
  const [selectedProject, setSelectedProject] = useState<ProjectItem | null>(null);
  const [selectedEquipment, setSelectedEquipment] = useState<EquipmentItem | null>(null);
  const [projectCategory, setProjectCategory] = useState<string>('ALL');

  const [projects, setProjects] = useState<ProjectItem[]>(INITIAL_PROJECTS);
  const [equipment, setEquipment] = useState<EquipmentItem[]>(INITIAL_EQUIPMENT);
  const [events, setEvents] = useState<EventItem[]>(INITIAL_EVENTS);
  const [distribution, setDistribution] = useState<DistributionTitle[]>(INITIAL_DISTRIBUTION);
  const [news, setNews] = useState<NewsArticle[]>(INITIAL_NEWS);
  const [partners, setPartners] = useState<PartnerItem[]>([]);
  const [team, setTeam] = useState<TeamMember[]>(INITIAL_TEAM_MEMBERS);
  const [siteContent, setSiteContent] = useState<SiteContentConfig | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadAllContent() {
      // 1. Projects
      try {
        const cmsProjects = await getProjects();
        const baseProjects = cmsProjects && cmsProjects.length > 0 ? cmsProjects : INITIAL_PROJECTS;
        const resolved = await Promise.all(baseProjects.map(resolveProjectMedia));
        if (isMounted) setProjects(resolved);
      } catch (err) {
        console.warn('Error loading projects, using defaults:', err);
      }

      // 2. Equipment
      try {
        const cmsEq = await getEquipment();
        const baseEq = cmsEq && cmsEq.length > 0 ? cmsEq : INITIAL_EQUIPMENT;
        const resolved = await Promise.all(baseEq.map(resolveEquipmentMedia));
        if (isMounted) setEquipment(resolved);
      } catch (err) {
        console.warn('Error loading equipment:', err);
      }

      // 3. Events
      try {
        const cmsEvents = await getEvents();
        const baseEvents = cmsEvents && cmsEvents.length > 0 ? cmsEvents : INITIAL_EVENTS;
        const resolved = await Promise.all(baseEvents.map(resolveEventMedia));
        if (isMounted) setEvents(resolved);
      } catch (err) {
        console.warn('Error loading events:', err);
      }

      // 4. Distribution
      try {
        const cmsDist = await getDistribution();
        const baseDist = cmsDist && cmsDist.length > 0 ? cmsDist : INITIAL_DISTRIBUTION;
        const resolved = await Promise.all(baseDist.map(resolveDistributionMedia));
        if (isMounted) setDistribution(resolved);
      } catch (err) {
        console.warn('Error loading distribution:', err);
      }

      // 5. News
      try {
        const cmsNews = await getNewsArticles();
        const baseNews = cmsNews && cmsNews.length > 0 ? cmsNews : INITIAL_NEWS;
        const resolved = await Promise.all(baseNews.map(resolveNewsMedia));
        if (isMounted) setNews(resolved);
      } catch (err) {
        console.warn('Error loading news:', err);
      }

      // 6. Partners
      try {
        const cmsPartners = await getPartners();
        if (isMounted && cmsPartners) setPartners(cmsPartners.filter(p => p.status === 'PUBLISHED'));
      } catch (err) {
        console.warn('Error loading partners:', err);
      }

      // 7. Team
      try {
        const cmsTeam = await getTeam();
        if (isMounted && cmsTeam && cmsTeam.length > 0) {
          setTeam(cmsTeam);
        }
      } catch (err) {
        console.warn('Error loading team:', err);
      }

      // 8. Site Content / Narrative
      try {
        const content = await getSiteContent();
        if (isMounted && content) setSiteContent(content);
      } catch (err) {
        console.warn('Error loading site content:', err);
      }
    }

    loadAllContent();

    return () => {
      isMounted = false;
    };
  }, []);

  const publishedProjects = projects.filter((p) => p.status === 'PUBLISHED');
  const publishedEquipment = equipment.filter((e) => e.status === 'PUBLISHED');
  const publishedEvents = events.filter((e) => e.status === 'PUBLISHED');
  const publishedNews = news.filter((n) => n.status === 'PUBLISHED');
  const publishedDistribution = distribution.filter((d) => d.status === 'PUBLISHED');
  const publishedTeam = team.filter((t) => t.status === 'PUBLISHED');

  const filteredProjects = projectCategory === 'ALL'
    ? publishedProjects
    : publishedProjects.filter((p) => p.genre.toUpperCase().includes(projectCategory.toUpperCase()));

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-[#18181B] font-sans selection:bg-[#D97706] selection:text-white relative">
      <CustomCursor />
      <PublicHeader />

      {/* 01 CINEMATIC BANNER HERO SLIDER */}
      <HeroSlider />

      {/* 02 BRAND STATEMENT */}
      <section className="py-24 bg-white border-b border-zinc-200/80 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center space-y-6">
          <span className="text-xs font-mono text-[#B45309] font-bold uppercase tracking-widest block">
            {siteContent?.brandStatement?.eyebrow || siteContent?.homepageSections?.brandStatement?.eyebrow || '02 // BRAND VISION & PURPOSE'}
          </span>
          <h2 className="text-3xl sm:text-5xl font-bold font-serif-cinematic text-zinc-900 leading-tight">
            {siteContent?.brandStatement?.heading || siteContent?.aboutStudioTitle || '“WE BELIEVE IN THE TRANSFORMATIVE POWER OF CINEMA TO CONNECT PEOPLE, SHAPE PERSPECTIVES, AND INFLUENCE CULTURE.”'}
          </h2>
          <p className="text-zinc-600 font-sans text-base sm:text-lg max-w-3xl mx-auto leading-relaxed">
            {siteContent?.brandStatement?.description || siteContent?.aboutStudioText || 'Our vision is to create meaningful, globally relevant stories that combine creative excellence, innovation, and emotional impact—building a future where our work transcends screens and leaves a lasting cultural footprint.'}
          </p>
        </div>
      </section>

      {/* 03 CATEGORY CARDS / DIVISIONS */}
      <StargazeUniverse3D initialConfig={siteContent?.universeSection} />

      {/* 04 FEATURED WORK / CINEMA SLATE */}
      <section id="slate" className="py-24 bg-[#F8F9FA] border-t border-zinc-200/80 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-12">
          {/* Header & Filter */}
          <div className="flex flex-col md:flex-row md:items-end justify-between border-b border-zinc-200 pb-6 gap-4">
            <div>
              <span className="text-[#B45309] text-xs font-mono font-bold tracking-widest uppercase block mb-2">
                {siteContent?.homepageSections?.featuredWork?.eyebrow || '04 // FEATURED WORK'}
              </span>
              <h2 className="text-3xl sm:text-5xl font-black font-serif-cinematic text-zinc-900 uppercase">
                {siteContent?.homepageSections?.featuredWork?.heading || 'FEATURED FILM SLATE'}
              </h2>
            </div>

            {/* Category Filter Pills */}
            <div className="flex flex-wrap gap-2 font-mono text-xs">
              {['ALL', 'SCI-FI', 'DRAMA', 'DOCUMENTARY'].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setProjectCategory(cat)}
                  className={`px-3.5 py-1.5 rounded-full border transition ${
                    projectCategory === cat
                      ? 'bg-[#D97706] text-white border-[#D97706] font-bold shadow-xs'
                      : 'bg-white text-zinc-600 border-zinc-200 hover:border-amber-400 hover:text-zinc-900'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Project Slate Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <div
                key={project.id}
                onClick={() => setSelectedProject(project)}
                data-cursor="PLAY"
                className="group relative bg-white border border-zinc-200/90 rounded-2xl overflow-hidden hover:border-[#D97706] hover:shadow-xl transition duration-500 cursor-pointer flex flex-col justify-between"
              >
                <div className="aspect-[16/10] overflow-hidden relative bg-zinc-100">
                  <StargazeImage
                    src={project.posterUrl}
                    alt={project.title}
                    focalPoint={project.focalPoint}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <span className="absolute top-3 right-3 bg-[#D97706] text-white font-mono text-[10px] font-extrabold px-2.5 py-1 rounded uppercase tracking-wider shadow-xs">
                    {project.genre.split('/')[0]}
                  </span>

                  {/* Play Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition duration-300 bg-black/40">
                    <div className="w-14 h-14 rounded-full bg-[#D97706] flex items-center justify-center text-white shadow-xl">
                      <Play className="w-6 h-6 fill-white translate-x-0.5" />
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-3">
                  <h3 className="text-xl font-bold font-serif-cinematic text-zinc-900 group-hover:text-[#D97706] transition">
                    {project.title}
                  </h3>
                  <p className="text-xs text-zinc-600 font-sans line-clamp-2 leading-relaxed">
                    {project.synopsis}
                  </p>

                  <div className="pt-4 border-t border-zinc-100 flex items-center justify-between text-xs font-mono text-zinc-500">
                    <span>Director: {project.director}</span>
                    <span className="text-[#B45309] font-bold">{project.releaseYear}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 05 PRODUCTION TIMELINE */}
      <ProductionTimeline />

      {/* 06 POST-PRODUCTION TECHNICAL SUITE */}
      <PostProductionSuite />

      {/* 07 EQUIPMENT RENTAL SHOWCASE */}
      {siteContent?.homepageSections?.equipment?.enabled !== false && (() => {
        const eqConfig = siteContent?.homepageSections?.equipment;
        const bgHex = eqConfig?.backgroundColor || '#F8F9FA';
        const isDark = bgHex.startsWith('#0') || bgHex.startsWith('#1') || bgHex === 'black';
        const accentHex = eqConfig?.accentColor || '#B45309';

        return (
          <section
            style={{ backgroundColor: bgHex }}
            className={`py-24 border-t border-zinc-200/80 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
              isDark ? 'text-white border-zinc-800' : 'text-zinc-900'
            }`}
          >
            <div className="max-w-7xl mx-auto space-y-12">
              <div className={`flex flex-col md:flex-row md:items-end justify-between border-b pb-6 gap-4 ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
                <div>
                  <span
                    style={{ color: accentHex }}
                    className="text-xs font-mono font-bold tracking-widest uppercase block mb-2"
                  >
                    {eqConfig?.eyebrow || '07 // HARDWARE & OPTICS'}
                  </span>
                  <h2 className={`text-3xl sm:text-5xl font-black font-serif-cinematic uppercase ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                    {eqConfig?.heading || 'CAMERA & DOP RENTAL DIVISION'}
                  </h2>
                  {eqConfig?.description && (
                    <p className={`text-sm mt-2 max-w-2xl font-sans leading-relaxed ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                      {eqConfig.description}
                    </p>
                  )}
                </div>
                <a
                  href={eqConfig?.ctaDestination || '/equipment'}
                  style={{ color: accentHex }}
                  className="text-xs font-mono font-bold tracking-wider hover:opacity-80 flex items-center gap-1 transition"
                >
                  {eqConfig?.ctaLabel || 'BROWSE CATALOGUE'} <ChevronRight className="w-4 h-4" />
                </a>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {publishedEquipment.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => setSelectedEquipment(item)}
                    data-cursor="EXPLORE"
                    className={`rounded-2xl p-5 border transition duration-300 cursor-pointer flex flex-col justify-between ${
                      isDark
                        ? 'bg-zinc-900/90 border-zinc-800 hover:border-amber-500/60 hover:shadow-xl hover:shadow-black/50 text-white'
                        : 'bg-white border-zinc-200 hover:border-[#D97706] hover:shadow-lg text-zinc-900'
                    }`}
                  >
                    <div>
                      <div className="aspect-[4/3] rounded-xl overflow-hidden mb-4 bg-zinc-100 border border-zinc-100">
                        <StargazeImage src={item.imageUrl} alt={item.name} focalPoint={item.focalPoint} className="w-full h-full object-cover" />
                      </div>
                      <span
                        style={{ color: accentHex }}
                        className="text-[10px] font-mono font-bold uppercase tracking-widest block mb-1"
                      >
                        {item.category}
                      </span>
                      <h4 className={`text-sm font-bold font-serif-cinematic mb-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                        {item.name}
                      </h4>
                      <p className={`text-xs leading-relaxed mb-4 line-clamp-2 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                        {item.specs}
                      </p>
                    </div>

                    <div className={`pt-3 border-t flex items-center justify-between text-xs font-mono ${isDark ? 'border-zinc-800' : 'border-zinc-100'}`}>
                      <span style={{ color: accentHex }} className="font-bold">
                        ${item.dailyRate}/day
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          item.availability === 'AVAILABLE'
                            ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/30'
                            : 'bg-zinc-800 text-zinc-400'
                        }`}
                      >
                        {item.availability}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      })()}

      {/* 08 DISTRIBUTION CATALOGUE */}
      <section className="py-24 bg-white border-t border-zinc-200/80 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between border-b border-zinc-200 pb-6 gap-4">
            <div>
              <span className="text-[#B45309] text-xs font-mono font-bold tracking-widest uppercase block mb-2">
                {siteContent?.homepageSections?.distribution?.eyebrow || '08 // GLOBAL LICENSING'}
              </span>
              <h2 className="text-3xl sm:text-5xl font-black font-serif-cinematic text-zinc-900 uppercase">
                {siteContent?.homepageSections?.distribution?.heading || 'DISTRIBUTION CATALOGUE'}
              </h2>
            </div>
            <a
              href={siteContent?.homepageSections?.distribution?.ctaDestination || '/distribution'}
              className="text-xs font-mono font-bold tracking-wider text-[#B45309] hover:text-[#D97706] flex items-center gap-1"
            >
              {siteContent?.homepageSections?.distribution?.ctaLabel || 'VIEW FULL CATALOGUE'} <ChevronRight className="w-4 h-4" />
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {publishedDistribution.map((dist) => (
              <div
                key={dist.id}
                className="bg-[#F8F9FA] border border-zinc-200 rounded-2xl p-6 flex flex-col sm:flex-row gap-6 hover:border-[#D97706]/60 hover:shadow-md transition"
              >
                <div className="w-full sm:w-44 aspect-[3/4] rounded-xl overflow-hidden shrink-0 bg-zinc-200">
                  <StargazeImage src={dist.posterUrl} alt={dist.title} focalPoint={dist.focalPoint} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 flex flex-col justify-between space-y-3">
                  <div>
                    <span className="px-2.5 py-1 rounded bg-amber-500/10 text-[#B45309] border border-amber-500/30 font-mono text-[10px] font-bold uppercase tracking-wider inline-block mb-2">
                      {dist.type} // RIGHTS AVAILABLE
                    </span>
                    <h3 className="text-xl font-bold font-serif-cinematic text-zinc-900">{dist.title}</h3>
                    <p className="text-xs text-zinc-600 font-sans leading-relaxed mt-2">{dist.synopsis}</p>
                  </div>

                  <div className="space-y-2 pt-3 border-t border-zinc-200 text-xs font-mono">
                    <div className="flex items-center gap-2 text-zinc-700">
                      <Globe className="w-4 h-4 text-[#D97706]" />
                      <span>Territories: <strong className="text-zinc-900">{dist.territories.join(', ')}</strong></span>
                    </div>
                    <div className="flex items-center gap-2 text-zinc-700">
                      <CheckCircle2 className="w-4 h-4 text-[#D97706]" />
                      <span>Rights: <strong className="text-zinc-900">{dist.rightsAvailable.join(' • ')}</strong></span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRY ALLIANCES & PARTNERS (Dynamic CMS) */}
      {partners.length > 0 && (
        <section className="py-20 bg-zinc-50 border-t border-zinc-200/80 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto space-y-10">
            <div className="flex flex-col md:flex-row md:items-end justify-between border-b border-zinc-200 pb-6 gap-4">
              <div>
                <span className="text-[#B45309] text-xs font-mono font-bold tracking-widest uppercase block mb-2">
                  {siteContent?.homepageSections?.partners?.eyebrow || 'INDUSTRY COLLABORATORS & ALLIANCES'}
                </span>
                <h2 className="text-3xl sm:text-4xl font-black font-serif-cinematic text-zinc-900 uppercase">
                  {siteContent?.homepageSections?.partners?.heading || 'CO-PRODUCERS & PARTNERS'}
                </h2>
              </div>
              <p className="text-xs font-mono text-zinc-500 max-w-md">
                {siteContent?.homepageSections?.partners?.description || 'Institutional, governmental, and production alliances powering Stargaze cinema.'}
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
              {partners.map((partner) => (
                <div
                  key={partner.id}
                  className="bg-white border border-zinc-200 rounded-2xl p-6 flex flex-col items-center justify-center text-center space-y-3 hover:border-[#D97706] hover:shadow-md transition group"
                >
                  <div className="h-16 w-full flex items-center justify-center">
                    {partner.logoUrl ? (
                      <StargazeImage
                        src={partner.logoUrl}
                        alt={partner.name}
                        className="max-h-12 max-w-[85%] object-contain filter grayscale group-hover:grayscale-0 transition duration-300"
                      />
                    ) : (
                      <span className="text-xs font-mono font-bold text-[#B45309] uppercase tracking-wider">
                        {partner.name}
                      </span>
                    )}
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-zinc-900 uppercase group-hover:text-[#D97706] transition">
                      {partner.name}
                    </h4>
                    <span className="text-[10px] font-mono text-zinc-500 uppercase block mt-0.5">
                      {partner.category}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 09 EXPERIENCES */}
      <section className="py-24 bg-white border-t border-zinc-200/80 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between border-b border-zinc-200 pb-6 gap-4">
            <div>
              <span className="text-[#B45309] text-xs font-mono font-bold tracking-widest uppercase block mb-2">
                {siteContent?.homepageSections?.experiences?.eyebrow || '09 // LIVE ACTIVATIONS'}
              </span>
              <h2 className="text-3xl sm:text-5xl font-black font-serif-cinematic text-zinc-900 uppercase">
                {siteContent?.homepageSections?.experiences?.heading || 'PREMIERES & EXPERIENCES'}
              </h2>
            </div>
            <a
              href={siteContent?.homepageSections?.experiences?.ctaDestination || '/events'}
              className="text-xs font-mono font-bold tracking-wider text-[#B45309] hover:text-[#D97706] flex items-center gap-1"
            >
              {siteContent?.homepageSections?.experiences?.ctaLabel || 'ALL EXPERIENCES'} <ChevronRight className="w-4 h-4" />
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {publishedEvents.map((evt) => (
              <div key={evt.id} className="group relative rounded-2xl overflow-hidden border border-zinc-200 bg-zinc-900 aspect-[16/9] shadow-md">
                <StargazeImage src={evt.imageUrl} alt={evt.title} focalPoint={evt.focalPoint} className="w-full h-full object-cover group-hover:scale-105 transition duration-700 opacity-75" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-6 space-y-2">
                  <div className="flex items-center gap-3 text-xs font-mono text-amber-300">
                    <span className="px-2.5 py-1 rounded bg-[#D97706] text-white font-bold uppercase shadow-xs">{evt.category}</span>
                    <span>{evt.eventDate} • {evt.location}</span>
                  </div>
                  <h3 className="text-2xl font-bold font-serif-cinematic text-white">{evt.title}</h3>
                  <p className="text-xs text-zinc-200 font-sans line-clamp-2">{evt.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10 NEWSROOM */}
      <section className="py-24 bg-[#F8F9FA] border-t border-zinc-200/80 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between border-b border-zinc-200 pb-6 gap-4">
            <div>
              <span className="text-[#B45309] text-xs font-mono font-bold tracking-widest uppercase block mb-2">
                {siteContent?.homepageSections?.newsroom?.eyebrow || '10 // PRESS & ANNOUNCEMENTS'}
              </span>
              <h2 className="text-3xl sm:text-5xl font-black font-serif-cinematic text-zinc-900 uppercase">
                {siteContent?.homepageSections?.newsroom?.heading || 'STARGAZE NEWSROOM'}
              </h2>
            </div>
            <a
              href={siteContent?.homepageSections?.newsroom?.ctaDestination || '/news'}
              className="text-xs font-mono font-bold tracking-wider text-[#B45309] hover:text-[#D97706] flex items-center gap-1"
            >
              {siteContent?.homepageSections?.newsroom?.ctaLabel || 'READ ALL INSIGHTS'} <ChevronRight className="w-4 h-4" />
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {publishedNews.map((news) => (
              <a
                key={news.id}
                href="/news"
                className="p-6 rounded-2xl bg-white border border-zinc-200 hover:border-[#D97706] hover:shadow-md transition space-y-3 block"
              >
                <div className="flex items-center justify-between text-xs font-mono text-[#B45309] font-bold">
                  <span>{news.category}</span>
                  <span>{news.publishDate}</span>
                </div>
                <h3 className="text-xl font-bold font-serif-cinematic text-zinc-900 hover:text-[#D97706] transition">
                  {news.title}
                </h3>
                <p className="text-xs text-zinc-600 font-sans leading-relaxed line-clamp-2">
                  {news.summary}
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* 11 TEAM SECTION */}
      {publishedTeam.length > 0 && (
        <section id="team" className="py-24 bg-white border-t border-zinc-200/80 px-4 sm:px-6 lg:px-8 scroll-mt-24">
          <div className="max-w-7xl mx-auto space-y-12">
            <div className="flex flex-col md:flex-row md:items-end justify-between border-b border-zinc-200 pb-6 gap-4">
              <div>
                <span className="text-[#B45309] text-xs font-mono font-bold tracking-widest uppercase block mb-2">
                  11 // EXECUTIVE LEADERSHIP & FOUNDERS
                </span>
                <h2 className="text-3xl sm:text-5xl font-black font-serif-cinematic text-zinc-900 uppercase">
                  THE STARGAZE TEAM
                </h2>
              </div>
              <p className="text-xs font-mono text-zinc-500 max-w-md">
                Visionary producers, musical maestros, and operational directors behind Stargaze Media and Orange City Production.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {publishedTeam.map((member) => (
                <div
                  key={member.id}
                  className="bg-[#F8F9FA] border border-zinc-200 rounded-2xl overflow-hidden hover:border-[#D97706] hover:shadow-xl transition duration-300 flex flex-col justify-between group"
                >
                  <div className="aspect-[4/5] relative overflow-hidden bg-zinc-200">
                    {member.portraitUrl || member.photoUrl ? (
                      <StargazeImage
                        src={member.portraitUrl || member.photoUrl || ''}
                        alt={member.name}
                        focalPoint={member.focalPoint}
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-zinc-100 to-zinc-200 text-zinc-400 p-6 text-center">
                        <Users className="w-12 h-12 mb-2 text-[#D97706]/60" />
                        <span className="text-xs font-mono font-bold uppercase tracking-wider text-zinc-700">
                          {member.name}
                        </span>
                      </div>
                    )}
                    <span className="absolute top-3 left-3 bg-white/90 backdrop-blur-md text-[#B45309] font-mono text-[10px] font-bold px-2 py-0.5 rounded border border-zinc-200 uppercase">
                      {member.department || 'STUDIO LEADERSHIP'}
                    </span>
                  </div>

                  <div className="p-6 space-y-2">
                    <h3 className="text-lg font-bold font-serif-cinematic text-zinc-900 group-hover:text-[#D97706] transition">
                      {member.name}
                    </h3>
                    <p className="text-xs font-mono font-bold text-[#B45309] uppercase tracking-wider">
                      {member.role}
                    </p>
                    <p className="text-xs text-zinc-600 font-sans leading-relaxed pt-1 line-clamp-3">
                      {member.shortBio || member.fullBio || member.bio}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 12 FINAL CTA */}
      <section className="py-24 bg-gradient-to-b from-white to-amber-50/40 border-t border-zinc-200 text-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <span className="text-xs font-mono text-[#B45309] font-bold uppercase tracking-widest block">
            {siteContent?.homepageSections?.finalCta?.eyebrow || '11 // INITIATE COLLABORATION'}
          </span>
          <h2 className="text-4xl sm:text-6xl font-black font-serif-cinematic text-zinc-900 uppercase leading-tight">
            {siteContent?.homepageSections?.finalCta?.heading || (
              <>
                LET’S MAKE SOMETHING<br />
                <span className="text-[#D97706]">WORTH WATCHING</span>
              </>
            )}
          </h2>
          <p className="text-zinc-600 max-w-xl mx-auto text-base">
            {siteContent?.homepageSections?.finalCta?.description || 'From co-productions and equipment rental to post-production and global distribution licensing.'}
          </p>
          <div className="pt-4">
            <a
              href={siteContent?.homepageSections?.finalCta?.ctaDestination || '/enquiry'}
              data-cursor="ENQUIRE"
              className="inline-flex items-center gap-3 px-10 py-5 rounded-2xl bg-[#D97706] hover:bg-[#B45309] text-white font-mono font-bold text-sm uppercase tracking-widest transition shadow-xl shadow-amber-600/25 hover:scale-105"
            >
              {siteContent?.homepageSections?.finalCta?.ctaLabel || 'START A PROJECT'} <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Modals */}
      <ProjectDetailModal
        project={selectedProject}
        onClose={() => setSelectedProject(null)}
      />
      <EquipmentDetailModal
        item={selectedEquipment}
        onClose={() => setSelectedEquipment(null)}
      />

      <PublicFooter />
    </div>
  );
};
